import React, { useState, useEffect, useContext, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Account from './components/Account';
import Market from './components/Market';
import Events from './components/Events';
import BottomTicker from './components/BottomTicker';
import CompactHeader from './components/CompactHeader';
import QRScanner from './components/QRScanner';
import Leaderboard from './components/Leaderboard';
import NavBar from './components/NavBar';
import Login from './components/Login';
import ProfileSetup from './components/ProfileSetup';
import ProfileManagement from './components/ProfileManagement';
import { initSocket } from './services/socket';
import { validateUserSession, checkProfileStatus } from './services/auth';
import { ProModeProvider } from './context/ProModeContext';
import { NotificationProvider } from './context/NotificationContext';
// JacketRegistration removed - stretch goal for future parties
import AdminPanel from './components/AdminPanel';
import AdminProtected from './components/AdminProtected';
import AdminNotifications from './components/AdminNotifications';
import { ToastManager, showToast } from './components/ToastNotification';
import SelfRegister from './components/SelfRegister';
import PartyEntranceQR from './components/PartyEntranceQR';
import GuestQRLogin from './components/GuestQRLogin';
import RegistrationCodesDisplay from './components/RegistrationCodesDisplay';
import BadgePrint from './components/BadgePrint';
import BadgeStation from './components/BadgeStation';
import VideoPage from './components/VideoPage';
import VideoHub from './components/VideoHub';
import Workshop from './components/Workshop';
import Calendar from './components/Calendar';

import TerminalInvite from './components/TerminalInvite';
import LandingPage from './components/LandingPage';
import TerminalMessage from './components/TerminalMessage';
import CinematicMode from './components/CinematicMode';
import HomePage from './components/HomePage';
import Overview from './components/Overview';
import FAQPage from './components/FAQPage';
import MinigamesPage from './pages/MinigamesPage';
import LoginPage from './components/pages/LoginPage';
import StockDetailPage from './components/StockDetailPage';
import PasswordGate from './components/PasswordGate';
import LoadingIndicator from './components/LoadingIndicator';
import ErrorBoundary from './components/ErrorBoundary';
import AdminBattleshipPage from './pages/AdminBattleshipPage';
import QRCodeDisplay from './components/QRCodeDisplay';
import BusinessCard from './components/BusinessCard';
import CodeRegister from './components/CodeRegister';
import TermsPage from './components/TermsPage';
import BattleshipSpectatorPage from './pages/BattleshipSpectatorPage';
import BattleshipMonopolyBoard from './components/BattleshipMonopolyBoard';
import BriefcaseSpectatorPage from './pages/BriefcaseSpectatorPage';
import MinigolfSpectatorPage from './pages/MinigolfSpectatorPage';
// SpectatorIndexPage removed - spectate access removed from user-facing pages
import GamesPage from './pages/GamesPage';
import BattleshipPlayPage from './pages/BattleshipPlayPage';
import BattleshipTVPage from './pages/BattleshipTVPage';
import CeremonySpectatorPage from './pages/CeremonySpectatorPage';
import { API_BASE_URL } from './config';

// Import embed components for broadcast overlays
import {
    EmbedStockChart,
    EmbedMultiStockChart,
    EmbedTickerTape,
    EmbedEventsTicker,
    EmbedLeaderboard,
    EmbedPrice,
    EmbedMarket,
    EmbedCountdown,
    EmbedBottomTicker,
    EmbedStockDetail,
    EmbedRecentTrades,
    EmbedPortfolio,
    EmbedVideo,
    EmbedContent,
    EmbedBriefcaseLeaderboard,
    EmbedRandomStocks,
    EmbedDashboard,
    EmbedTradesTable,
    EmbedEventsTable,
    EmbedRandomDetail,
    EmbedLeaderboardLogo,
    EmbedHome,
    EmbedShuffle,
    EmbedMultiScreen
} from './components/embed';

// Import styles
import './styles/main.css';
import './styles/pro.css';
import './styles/invite.css';
import './styles/candlestick-chart.css';
import './styles/stock-detail.css';
import './styles/orderbook.css';
import './styles/ticker-tape.css';
import './styles/trading-terminal.css';

// DEVELOPMENT_MODE - Set to true when developing locally
// Set to false before deploying to production
const DEVELOPMENT_MODE = true;

// Route checker component to display invitation or terminal message
function RouteChecker() {
  const location = useLocation();
  
  // Check if we're on the home page or not
  const isHomePage = location.pathname === '/' || location.pathname === '';
  
  // If we're in development mode, don't show any special screens
  if (DEVELOPMENT_MODE) {
    return null;
  }
  
  // When not in development mode:
  // - Home page: Show invitation
  // - Any other route: Show terminal message
  if (isHomePage) {
    return <TerminalInvite onComplete={() => {}} />;
  } else {
    return <TerminalMessage onReturnToInvite={() => window.location.href = '/'} />;
  }
}

// Auth wrapper component to protect routes that require authentication
const AuthRequired = ({ children, isAuthenticated, onLoginRedirect }) => {
  if (!isAuthenticated) {
    // Store the current path for redirect after login
    sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Context to share App state with stable child components (prevents unmount/remount)
const AppStateContext = React.createContext({});

// Fullscreen toggle button for kiosk/TV displays
function FullscreenButton() {
    const [isFs, setIsFs] = useState(false);
    useEffect(() => {
        const onChange = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    if (isFs) return null;
    return (
        <button onClick={() => document.documentElement.requestFullscreen()} style={{
            position: 'fixed', top: 8, left: 8, zIndex: 99999,
            background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
            color: '#00ff41', padding: '4px 10px', fontSize: '11px',
            fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
        }}>FULLSCREEN</button>
    );
}

// Embed routes wrapper - renders embed components without any app chrome
// Defined OUTSIDE App so React keeps a stable reference (no unmount/remount)
function EmbedRoutes() {
    return (
        <>
        <FullscreenButton />
        <Routes>
            <Route path="/embed/stock/:ticker" element={<EmbedStockChart />} />
            <Route path="/embed/stocks/:tickers" element={<EmbedMultiStockChart />} />
            <Route path="/embed/ticker" element={<EmbedTickerTape />} />
            <Route path="/embed/events-ticker" element={<EmbedEventsTicker />} />
            <Route path="/embed/leaderboard" element={<EmbedLeaderboard />} />
            <Route path="/embed/countdown" element={<EmbedCountdown />} />
            <Route path="/embed/price/:ticker" element={<EmbedPrice />} />
            <Route path="/embed/market" element={<EmbedMarket />} />
            <Route path="/embed/full-market" element={<EmbedMarket fullMarket={true} defaultShowCharts={true} />} />
            <Route path="/embed/full-market-compact" element={<EmbedMarket fullMarket={true} defaultShowCharts={false} />} />
            <Route path="/embed/full-market-box" element={<EmbedMarket fullMarket={true} boxStyle={true} defaultShowCharts={true} />} />
            <Route path="/embed/video" element={<EmbedVideo />} />
            <Route path="/embed/content/:contentId" element={<EmbedContent />} />
            <Route path="/embed/briefcase" element={<EmbedBriefcaseLeaderboard />} />
            <Route path="/embed/random-stocks" element={<EmbedRandomStocks />} />
            <Route path="/embed/dashboard" element={<EmbedDashboard />} />
            <Route path="/embed/trades-table" element={<EmbedTradesTable />} />
            <Route path="/embed/events-table" element={<EmbedEventsTable />} />
            <Route path="/embed/random-detail" element={<EmbedRandomDetail />} />
            <Route path="/embed/leaderboard-logo" element={<EmbedLeaderboardLogo />} />
            <Route path="/embed/home" element={<EmbedHome />} />
            <Route path="/embed/shuffle" element={<EmbedShuffle />} />
            <Route path="/embed/multi" element={<EmbedMultiScreen />} />
            <Route path="/embed/reg-video" element={<Navigate to="/embed/content/registration?loop=true&muted=true&autoplay=true" replace />} />
            <Route path="/reg-video" element={<Navigate to="/embed/content/registration?loop=true&muted=true&autoplay=true" replace />} />
            <Route path="/qr/:code" element={<QRCodeDisplay />} />
            <Route path="/spectate/tv" element={<BattleshipTVPage />} />
            <Route path="/spectate/battleship" element={<BattleshipSpectatorPage />} />
            <Route path="/spectate/deadcat" element={<MinigolfSpectatorPage />} />
            <Route path="/spectate/briefcase" element={<BriefcaseSpectatorPage />} />
            <Route path="/spectate/ceremony" element={<CeremonySpectatorPage />} />
            <Route path="/badge-station" element={<BadgeStation />} />
            <Route path="/registration-codes" element={<RegistrationCodesDisplay />} />
            <Route path="/cinematic" element={<CinematicMode />} />
            <Route path="/terms" element={<TermsPage />} />
        </Routes>
        </>
    );
}

// Main app content - defined OUTSIDE App so React keeps a stable reference
function AppContent() {
    const ctx = useContext(AppStateContext);
    const location = useLocation();
    const isLandingPage = location.pathname === '/' || location.pathname === '';
    const isInvitePage = location.pathname === '/invite';
    const isCardPage = location.pathname === '/card';
    const isSpectatePage = location.pathname.startsWith('/spectate');
    const isLoginPage = location.pathname === '/login';

    // Show password gate if no access AND not on landing/invite/card/spectate/login pages
    if (!ctx.hasAccess && !isLandingPage && !isInvitePage && !isCardPage && !isSpectatePage && !isLoginPage) {
        return <PasswordGate onCorrectPassword={ctx.handlePasswordCorrect} />;
    }

    // Public landing — three-button terminal menu
    if (isLandingPage) {
        return <LandingPage />;
    }

    // If on invite page, render TerminalInvite directly without any wrapper
    if (isInvitePage) {
        return <TerminalInvite onComplete={() => {}} />;
    }

    // If on card page, render BusinessCard directly without any wrapper
    if (isCardPage) {
        return <BusinessCard />;
    }

    // If on login page without access, render login directly without nav
    if (isLoginPage && !ctx.isSessionValid) {
        return (
            <div className="app-container pro-mode" style={{background: '#0a0a0a', minHeight: '100vh'}}>
                <LoginPage onLogin={ctx.handleAuth} />
            </div>
        );
    }

    // Show UI elements only if has access AND not on invite/badge-print page
    const isBadgePrintPage = window.location.pathname.startsWith('/badge-print');
    const shouldShowUI = ctx.hasAccess && !isInvitePage && !isBadgePrintPage;

    // Check if we're on certain pages to hide compact header
    const isOnHomePage = window.location.pathname === '/' || window.location.pathname === '/home';
    const isOnVideoPage = window.location.pathname === '/videos' || window.location.pathname === '/broadcast';

    return (
        <div className={`app-container pro-mode ${shouldShowUI ? 'has-ticker has-header' : ''}`}>
            {/* Navigation bar - Fixed at TOP - Only show if has access and not on invite page */}
            {shouldShowUI && (
                <NavBar
                    activeTab={ctx.activeTab}
                    onTabChange={ctx.setActiveTab}
                    isLoggedIn={ctx.isSessionValid}
                    onLogout={ctx.handleLogout}
                    onLogin={ctx.handleToggleLoginModal}
                    onProfileClick={ctx.handleToggleProfileManager}
                    marketState={ctx.marketState}
                />
            )}

            {/* Event Ticker - Only show if not on invite page and has access */}
            {shouldShowUI && <BottomTicker />}

            {/* Compact Header - Only show if not on invite page, has access, AND not on HomePage/video pages */}
            {shouldShowUI && !isOnHomePage && !isOnVideoPage && <CompactHeader userId={ctx.userId} isLoggedIn={ctx.isSessionValid} />}

            {/* Toast Notifications - ALWAYS VISIBLE on all pages */}
            <ToastManager />

            {/* Admin Notifications - Only show if has access */}
            {shouldShowUI && (
                <AdminNotifications
                    bigGameAnnouncement={ctx.bigGameAnnouncement}
                    adminNotification={ctx.adminNotification}
                    onDismissBigGame={() => ctx.setBigGameAnnouncement(null)}
                    onDismissNotification={() => ctx.setAdminNotification(null)}
                />
            )}

            {/* Profile Management Modal */}
            {ctx.showProfileManager && (
                <div className="profile-manager-overlay">
                    <ProfileManagement
                        userId={ctx.userId}
                        onClose={() => ctx.setShowProfileManager(false)}
                    />
                </div>
            )}

            {/* Login Modal */}
            {ctx.showLoginModal && (
                <div className="login-modal-overlay">
                    <div className="login-modal">
                        <button
                            className="close-modal"
                            onClick={() => ctx.setShowLoginModal(false)}
                        >
                            &times;
                        </button>
                        <Login
                            onLogin={ctx.handleAuth}
                            onRegisterClick={ctx.handleRegisterClick}
                        />
                    </div>
                </div>
            )}

            {/* Registration Modal */}
            {ctx.showRegister && (
                <div className="registration-modal-overlay">
                    <div className="registration-modal">
                        <button
                            className="close-modal"
                            onClick={() => ctx.setShowRegister(false)}
                        >
                            &times;
                        </button>
                        <div className="registration-header">
                            <h1>STOCK MARKET TERMINAL</h1>
                            <h2>TRADER REGISTRATION</h2>
                        </div>
                        <QRScanner onRegistration={ctx.handleAuth} />
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="main-content">
                <Routes>
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/loading" element={<LoadingIndicator />} />
                    <Route path="/card" element={<BusinessCard />} />

                    {/* Self-Service Registration Routes */}
                    <Route
                        path="/register-self"
                        element={
                            !ctx.isSessionValid ? (
                                <SelfRegister />
                            ) : (
                                <Navigate to="/market" replace />
                            )
                        }
                    />
                    <Route
                        path="/qr-login"
                        element={
                            !ctx.isSessionValid ? (
                                <GuestQRLogin onLogin={ctx.handleAuth} />
                            ) : (
                                <Navigate to="/market" replace />
                            )
                        }
                    />
                    <Route
                        path="/party-entrance-qr"
                        element={
                            <AdminProtected>
                                <PartyEntranceQR />
                            </AdminProtected>
                        }
                    />
                    <Route
                        path="/registration-codes"
                        element={<RegistrationCodesDisplay />}
                    />
                    <Route
                        path="/badge-print/:userId"
                        element={<BadgePrint />}
                    />

                    {/* Video pages */}
                    <Route
                        path="/broadcast"
                        element={<VideoPage videoId="broadcast" />}
                    />
                    <Route
                        path="/videos"
                        element={<VideoHub />}
                    />
                    <Route path="/workshop" element={<Workshop />} />
                    <Route path="/hackerspace" element={<Navigate to="/workshop" replace />} />
                    <Route path="/calendar" element={<Calendar />} />
                    <Route path="/upcoming" element={<Navigate to="/calendar" replace />} />

                    <Route path="/spectate/tv" element={<BattleshipTVPage />} />
                    <Route path="/spectate/battleship" element={<BattleshipSpectatorPage />} />
                    <Route path="/spectate/deadcat" element={<MinigolfSpectatorPage />} />
                    <Route path="/spectate/briefcase" element={<BriefcaseSpectatorPage />} />
                    <Route path="/spectate/ceremony" element={<CeremonySpectatorPage />} />
                            <Route path="/badge-station" element={<BadgeStation />} />

                    <Route
                        path="/login"
                        element={
                            !ctx.isSessionValid ? (
                                <LoginPage onLogin={ctx.handleAuth} />
                            ) : (
                                <Navigate to="/market" replace />
                            )
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            !ctx.isSessionValid ? (
                                <CodeRegister onLogin={ctx.handleAuth} />
                            ) : (
                                <Navigate to="/market" replace />
                            )
                        }
                    />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route
                        path="/qr-scanner"
                        element={<Navigate to="/scan" replace />}
                    />
                    <Route
                        path="/market"
                        element={
                            <ErrorBoundary componentName="Market">
                                {ctx.isSessionValid ?
                                    <Market userId={ctx.userId} marketState={ctx.marketState} /> :
                                    <Market isPublicView={true} marketState={ctx.marketState} />
                                }
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/stock/:stockId/:tab?"
                        element={
                            <ErrorBoundary componentName="Stock Detail">
                                <StockDetailPage
                                    userId={ctx.userId}
                                    positions={ctx.positions}
                                />
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/portfolio/:userId?"
                        element={
                            <ErrorBoundary componentName="Portfolio">
                                <Account userId={ctx.isSessionValid ? ctx.userId : null} marketState={ctx.marketState} />
                            </ErrorBoundary>
                        }
                    />
                    <Route path="/events" element={<Events />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/info" element={<FAQPage />} />
                    <Route
                        path="/leaderboard"
                        element={
                            <ErrorBoundary componentName="Leaderboard">
                                {ctx.isSessionValid ?
                                    <Leaderboard userId={ctx.userId} /> :
                                    <Leaderboard isPublicView={true} />
                                }
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/overview"
                        element={<Overview userId={ctx.userId} />}
                    />
                    <Route
                        path="/scan"
                        element={
                            <AuthRequired
                                isAuthenticated={ctx.isSessionValid}
                                onLoginRedirect={ctx.handleAuth}
                            >
                                <QRScanner userId={ctx.userId} />
                            </AuthRequired>
                        }
                    />
                    <Route
                        path="/minigames"
                        element={<MinigamesPage />}
                    />
                    <Route path="/games" element={<GamesPage />} />
                    <Route path="/games/battleship" element={<BattleshipPlayPage />} />
                    <Route path="/games/briefcase" element={<BriefcaseSpectatorPage />} />
                    <Route path="/games/minigolf" element={<MinigolfSpectatorPage />} />

                    <Route
                        path="/admin"
                        element={
                            <AdminProtected>
                                <AdminPanel />
                            </AdminProtected>
                        }
                    />
                    <Route
                        path="/admin/battleship-fullscreen"
                        element={
                            <AdminProtected>
                                <AdminBattleshipPage />
                            </AdminProtected>
                        }
                    />
                    <Route
                        path="/admin/battleship-monopoly"
                        element={
                            <AdminProtected>
                                <BattleshipMonopolyBoard isAdmin={true} />
                            </AdminProtected>
                        }
                    />
                    <Route
                        path="/admin/:tab"
                        element={
                            <AdminProtected>
                                <AdminPanel />
                            </AdminProtected>
                        }
                    />
                    <Route
                        path="/admin/:tab/:subtab"
                        element={
                            <AdminProtected>
                                <AdminPanel />
                            </AdminProtected>
                        }
                    />
                </Routes>
            </div>
        </div>
    );
}

// Main router - defined OUTSIDE App so React keeps a stable reference
function MainRouter() {
    const location = useLocation();
    const isEmbedQueryParam = new URLSearchParams(location.search).get('embed') === 'true';
    const isEmbedRoute = location.pathname.startsWith('/embed') || location.pathname.startsWith('/qr/') || location.pathname.startsWith('/spectate') || location.pathname === '/badge-station' || location.pathname === '/registration-codes' || location.pathname === '/terms' || location.pathname === '/reg-video' || location.pathname === '/cinematic';

    if (isEmbedRoute) {
        return <EmbedRoutes />;
    }

    return <AppContent />;
}

function App() {
    const [userId, setUserId] = useState(localStorage.getItem('userId'));
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [isSessionValid, setIsSessionValid] = useState(false);
    const [sessionChecked, setSessionChecked] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [activeTab, setActiveTab] = useState('market');
    const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
    const [showProfileManager, setShowProfileManager] = useState(false);
    const [profileChecked, setProfileChecked] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Password protection disabled — gate removed
    const [hasAccess] = useState(true);

    // Market state and admin notifications
    const [marketState, setMarketState] = useState({ isOpen: false, tradingEnabled: true });
    const [bigGameAnnouncement, setBigGameAnnouncement] = useState(null);
    const [adminNotification, setAdminNotification] = useState(null);

    // User positions for stock detail pages
    const [positions, setPositions] = useState([]);

    // Check if stored session is valid
    useEffect(() => {
        const validateSession = async () => {
            if (userId && token) {
                try {
                    // Attempt to validate the session
                    await validateUserSession(userId, token);
                    setIsSessionValid(true);

                    // Initialize socket with valid token
                    const socketInstance = initSocket(token);

                    // Add admin event listeners
                    socketInstance.on('market-bell', (data) => {
                        setMarketState(prev => ({ ...prev, isOpen: data.isOpen, tradingEnabled: data.isOpen }));
                        setAdminNotification({
                            type: data.type,
                            message: data.message,
                            timestamp: data.timestamp
                        });

                        // Auto-dismiss notification after 10 seconds
                        setTimeout(() => setAdminNotification(null), 10000);
                    });

                    // Keep market state in sync with party-state-update broadcasts
                    socketInstance.on('party-state-update', (data) => {
                        if (data.marketState && data.marketState.isOpen !== undefined) {
                            setMarketState(prev => ({
                                ...prev,
                                isOpen: data.marketState.isOpen,
                                ...(data.marketState.tradingEnabled !== undefined && { tradingEnabled: data.marketState.tradingEnabled }),
                            }));
                        }
                    });

                    socketInstance.on('big-game-announcement', (data) => {
                        setBigGameAnnouncement(data);
                        setMarketState(prev => ({ ...prev, tradingEnabled: !data.tradingPaused }));

                        // Auto-dismiss after 30 seconds
                        setTimeout(() => setBigGameAnnouncement(null), 30000);
                    });

                    socketInstance.on('trading-paused', (data) => {
                        setMarketState(prev => ({ ...prev, tradingEnabled: false }));
                        setAdminNotification({
                            type: 'TRADING_PAUSED',
                            message: data.message,
                            timestamp: data.timestamp
                        });

                        // Auto-dismiss notification after 5 seconds
                        setTimeout(() => setAdminNotification(null), 5000);
                    });

                    socketInstance.on('trading-resumed', (data) => {
                        setMarketState(prev => ({ ...prev, tradingEnabled: true }));
                        setAdminNotification({
                            type: 'TRADING_RESUMED',
                            message: data.message,
                            timestamp: data.timestamp
                        });

                        // Auto-dismiss notification after 5 seconds
                        setTimeout(() => setAdminNotification(null), 5000);
                    });

                    socketInstance.on('admin-balance-update', (data) => {
                        setAdminNotification({
                            type: 'ADMIN_BALANCE_UPDATE',
                            message: `Admin applied ${data.amount >= 0 ? 'bonus' : 'penalty'} of $${Math.abs(data.amount)} - ${data.reason}`,
                            timestamp: data.timestamp
                        });

                        // Auto-dismiss notification after 8 seconds
                        setTimeout(() => setAdminNotification(null), 8000);
                    });

                    // Check if user needs to complete profile setup
                    const profileStatus = await checkProfileStatus(userId);
                    setNeedsProfileSetup(!profileStatus.hasCompletedProfile);
                    setProfileChecked(true);

                    // Fetch initial market state (public endpoint)
                    try {
                        const marketResponse = await fetch(`${API_BASE_URL}/api/market/status`);
                        if (marketResponse.ok) {
                            const marketData = await marketResponse.json();
                            if (marketData.success && marketData.marketState) {
                                setMarketState(marketData.marketState);
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching initial market state:', error);
                    }

                    // Fetch user positions for stock detail pages
                    try {
                        const positionsResponse = await fetch(`${API_BASE_URL}/api/positions/${userId}`);
                        if (positionsResponse.ok) {
                            const positionsData = await positionsResponse.json();
                            setPositions(Array.isArray(positionsData) ? positionsData : []);
                        }
                    } catch (error) {
                        console.error('Error fetching user positions:', error);
                    }

                    // Listen for position updates via socket
                    socketInstance.on('position-update', (data) => {
                        if (data.userId === userId) {
                            // Refresh positions when user's positions change
                            fetch(`${API_BASE_URL}/api/positions/${userId}`)
                                .then(res => res.json())
                                .then(posData => setPositions(Array.isArray(posData) ? posData : []))
                                .catch(err => console.error('Error refreshing positions:', err));
                        }
                    });
                } catch (error) {
                    console.error('Session validation failed:', error);
                    // Clear invalid credentials
                    setUserId(null);
                    setToken(null);
                    localStorage.removeItem('userId');
                    localStorage.removeItem('token');
                    setIsSessionValid(false);
                    setProfileChecked(true);
                }
            } else {
                setIsSessionValid(false);
                setProfileChecked(true);

                // Fetch initial market state even for non-authenticated users (public endpoint)
                try {
                    const marketResponse = await fetch(`${API_BASE_URL}/api/market/status`);
                    if (marketResponse.ok) {
                        const marketData = await marketResponse.json();
                        if (marketData.success && marketData.marketState) {
                            setMarketState(marketData.marketState);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching initial market state:', error);
                }
            }
            setSessionChecked(true);
        };

        validateSession();
    }, [userId, token]);

    // Initialize a public socket for non-authenticated users so they receive events
    useEffect(() => {
        if (!userId && !window.socket) {
            const { initSocket } = require('./services/socket');
            initSocket(); // No token = public connection

            const basicSocket = require('./services/socket').default;

            basicSocket.on('market-bell', (data) => {
                setMarketState(prev => ({ ...prev, isOpen: data.isOpen, tradingEnabled: data.isOpen }));
                setAdminNotification({
                    type: data.type,
                    message: data.message,
                    timestamp: data.timestamp
                });
                setTimeout(() => setAdminNotification(null), 10000);
            });

            basicSocket.on('trading-paused', (data) => {
                setMarketState(prev => ({ ...prev, tradingEnabled: false }));
                setAdminNotification({
                    type: 'TRADING_PAUSED',
                    message: data.message,
                    timestamp: data.timestamp
                });
                setTimeout(() => setAdminNotification(null), 5000);
            });

            basicSocket.on('trading-resumed', (data) => {
                setMarketState(prev => ({ ...prev, tradingEnabled: true }));
                setAdminNotification({
                    type: 'TRADING_RESUMED',
                    message: data.message,
                    timestamp: data.timestamp
                });
                setTimeout(() => setAdminNotification(null), 5000);
            });

            basicSocket.on('party-state-update', (data) => {
                if (data.marketState && data.marketState.isOpen !== undefined) {
                    setMarketState(prev => ({
                        ...prev,
                        isOpen: data.marketState.isOpen,
                        ...(data.marketState.tradingEnabled !== undefined && { tradingEnabled: data.marketState.tradingEnabled }),
                    }));
                }
            });
        }
    }, [userId]);

    // Re-fetch market state on visibility change + poll every 15s as fallback
    useEffect(() => {
        const fetchMarketState = () => {
            fetch(`${API_BASE_URL}/api/market/status`)
                .then(res => res.ok ? res.json() : null)
                .then(data => {
                    if (data?.success && data.marketState) {
                        setMarketState(data.marketState);
                    }
                })
                .catch(err => console.error('Error fetching market state:', err));
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchMarketState();
            }
        };

        const pollInterval = setInterval(fetchMarketState, 15000);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            clearInterval(pollInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Listen for market events to display notification banner (all users including guests)
    useEffect(() => {
            const handleMarketEvent = (event) => {
              try {
                console.log('[App] Market event received:', event?.type, event?.description);

                // Determine event scope (market-wide, ticker-specific, or user-specific)
                let eventScope = 'market'; // default
                let toastType = 'info';

                // Check if this is a user-specific event
                if (event.userId || event.targetUser || event.affectedUser) {
                    eventScope = 'user';
                    toastType = 'user_event'; // Will be styled light blue
                }
                // Check if this is a ticker-specific event
                else if (event.affectedStocks && Array.isArray(event.affectedStocks) && event.affectedStocks.length > 0) {
                    // Check if it's NOT market-wide (ALL)
                    const firstStock = event.affectedStocks[0];
                    const isMarketWide = (firstStock === 'ALL' ||
                                         (typeof firstStock === 'object' && firstStock.ticker === 'ALL'));

                    if (!isMarketWide) {
                        eventScope = 'ticker';
                    } else {
                        eventScope = 'market';
                    }
                }
                else {
                    eventScope = 'market';
                }

                // Map known event types to toast types
                const eventTypeMap = {
                    'MARKET_OPEN': 'market_open',
                    'MARKET_CLOSE': 'market_close',
                    'bull_run': 'bull_run',
                    'market_crash': 'market_crash',
                    'volatility': 'volatility',
                    'stock_split': 'info',
                    'merger': 'info',
                    'earnings': 'info'
                };

                // Only use mapped types for specific known events
                if (eventTypeMap[event.type]) {
                    toastType = eventTypeMap[event.type];
                }

                // Extract the actual event title and format message
                let title = '';
                let message = '';
                let details = '';

                // Parse the event title from description
                if (event.type === 'bull_run') {
                    title = 'BULL RUN';
                } else if (event.type === 'market_crash') {
                    title = 'MARKET CRASH';
                } else if (event.type === 'volatility') {
                    title = 'HIGH VOLATILITY';
                } else if (event.type === 'MARKET_OPEN') {
                    title = 'MARKET OPEN';
                } else if (event.type === 'MARKET_CLOSE') {
                    title = 'MARKET CLOSED';
                } else if (event.type === 'stock_split') {
                    title = 'STOCK SPLIT';
                } else if (event.type === 'merger') {
                    title = 'MERGER ANNOUNCED';
                } else if (event.type === 'earnings') {
                    title = 'EARNINGS REPORT';
                } else if (event.type === 'custom' || event.type === 'CUSTOM' || event.type === 'custom_event') {
                    title = 'CUSTOM STOCK EVENT';

                    // For custom events, FORCE extract the percentage from ANY available field
                    if (!event.changePercent && !event.multiplier && !event.priceImpact && !event.impact && !event.change && !event.percentage) {
                        for (let key in event) {
                            if (event[key] && typeof event[key] === 'string' && event[key].includes('%')) {
                                const match = event[key].match(/([+-]?\d+\.?\d*)%/);
                                if (match) {
                                    event.extractedImpact = parseFloat(match[1]);
                                    break;
                                }
                            }
                        }
                    }
                } else {
                    title = event.type ? event.type.toUpperCase().replace(/_/g, ' ') : 'MARKET EVENT';
                }

                // ALWAYS show price impact as the main message if it exists
                let priceImpact = event.changePercent || event.multiplier || event.priceImpact || event.impact || event.change || event.percentage;

                // Try to extract from description if it contains percentage
                if (!priceImpact) {
                    if (event.description) {
                        const percentMatch = event.description.match(/([+-]?\d+\.?\d*)%/);
                        if (percentMatch) {
                            priceImpact = parseFloat(percentMatch[1]);
                        }
                    }

                    if (!priceImpact && event.message) {
                        const messageMatch = event.message.match(/([+-]?\d+\.?\d*)%/);
                        if (messageMatch) {
                            priceImpact = parseFloat(messageMatch[1]);
                        }
                    }
                }

                if (priceImpact !== undefined && priceImpact !== null && priceImpact !== 0) {
                    let percentChange;

                    if (typeof priceImpact === 'number') {
                        if (Math.abs(priceImpact) <= 1 && priceImpact !== 0 && priceImpact !== -1 && priceImpact !== 1) {
                            percentChange = ((priceImpact - 1) * 100).toFixed(1);
                        } else if (Math.abs(priceImpact) >= 1 && Math.abs(priceImpact) <= 100) {
                            percentChange = priceImpact.toFixed(1);
                        } else {
                            percentChange = ((priceImpact - 1) * 100).toFixed(1);
                        }
                    } else {
                        const parsed = parseFloat(String(priceImpact).replace('%', ''));
                        if (!isNaN(parsed)) {
                            percentChange = parsed.toFixed(1);
                        } else {
                            percentChange = '0.0';
                        }
                    }

                    const sign = parseFloat(percentChange) > 0 ? '+' : '';
                    const isPositiveImpact = parseFloat(percentChange) > 0;

                    if (eventScope === 'user') {
                        message = `${sign}${percentChange}% USER IMPACT`;
                        toastType = 'user_event';
                    } else if (eventScope === 'ticker') {
                        message = `${sign}${percentChange}% STOCK CHANGE`;
                        toastType = isPositiveImpact ? 'ticker_positive' : 'ticker_negative';
                    } else {
                        message = `${sign}${percentChange}% MARKET-WIDE`;
                        if (!eventTypeMap[event.type]) {
                            toastType = isPositiveImpact ? 'market_positive' : 'market_negative';
                        }
                    }

                    if (event.description && typeof event.description === 'string' &&
                        event.description !== 'No description' &&
                        event.description !== event.type &&
                        !event.description.match(/^[+-]?\d+\.?\d*%?$/)) {
                        details = event.description;
                    }
                } else {
                    if (event.description && typeof event.description === 'string' && event.description !== 'No description') {
                        message = event.description;
                    } else {
                        message = 'Market event triggered';
                    }
                    console.log('[App] NO PRICE IMPACT FOUND IN EVENT!');
                }

                // Add affected stocks to details
                if (event.affectedStocks && event.affectedStocks.length > 0) {
                    let stocksList = '';

                    if (typeof event.affectedStocks[0] === 'object') {
                        const tickers = event.affectedStocks.map(stock =>
                            stock.ticker || stock.symbol || stock.name || JSON.stringify(stock)
                        );
                        stocksList = `Affected: ${tickers.join(', ')}`;
                    } else {
                        stocksList = `Affected: ${event.affectedStocks.join(', ')}`;
                    }

                    details = details ? `${details} • ${stocksList}` : stocksList;
                }

                if (typeof message === 'object') {
                    message = 'Market event occurred';
                }

                showToast(message || 'Market event', toastType, 10000, details, title || 'MARKET EVENT');
              } catch (err) {
                console.error('[App] Error handling market event:', err);
                try {
                    showToast(
                        event?.description || 'A market event occurred',
                        'info',
                        10000,
                        null,
                        'MARKET EVENT'
                    );
                } catch (e) { /* prevent crash */ }
              }
            };

            const handleCountdownWarning = (data) => {
                const { message, warningType } = data;

                let toastType = 'warning';
                let title = 'Heads Up!';

                if (warningType === 'orientation') {
                    title = 'Orientation Starting Soon';
                    toastType = 'info';
                } else if (warningType === 'market-close') {
                    title = 'Market Closing Soon';
                    toastType = 'warning';
                } else if (warningType === 'macro-game') {
                    title = 'Game Starting Soon';
                    toastType = 'info';
                } else if (warningType === 'market-open') {
                    title = 'Market Opening Soon';
                    toastType = 'market_open';
                }

                showToast(message, toastType, 8000, null, title);
            };

            const handleTradingResumed = (data) => {
                const { sessionName, message } = data;

                showToast(
                    message || `Trading has resumed! ${sessionName} is now active.`,
                    'market_open',
                    6000,
                    'Get back to trading!',
                    'Trading Resumed'
                );
            };

            const socketService = require('./services/socket').default;
            console.log('[App] Registering market-event handler (userId:', userId, 'isSessionValid:', isSessionValid, ')');
            socketService.on('market-event', handleMarketEvent);
            socketService.on('countdown-warning', handleCountdownWarning);
            socketService.on('trading-resumed', handleTradingResumed);

            return () => {
                console.log('[App] Cleaning up market-event handler (userId:', userId, 'isSessionValid:', isSessionValid, ')');
                socketService.off('market-event', handleMarketEvent);
                socketService.off('countdown-warning', handleCountdownWarning);
                socketService.off('trading-resumed', handleTradingResumed);
            };
    }, [userId, isSessionValid]);

    // Handle user registration or login
    const handleAuth = (newUserId, newToken) => {
        setUserId(newUserId);
        setToken(newToken);
        localStorage.setItem('userId', newUserId);
        localStorage.setItem('token', newToken);

        // Initialize socket with new credentials
        initSocket(newToken);
        setIsSessionValid(true);
        setShowRegister(false);
        setShowLoginModal(false);

        // Check if user needs profile setup
        checkProfileStatus(newUserId)
            .then(status => {
                setNeedsProfileSetup(!status.hasCompletedProfile);
                setProfileChecked(true);

                // Redirect to the page they were trying to access before login
                const redirectPath = sessionStorage.getItem('redirectAfterLogin');
                if (redirectPath) {
                    window.location.href = redirectPath;
                    sessionStorage.removeItem('redirectAfterLogin');
                }
            })
            .catch(error => {
                console.error('Error checking profile status:', error);
                setProfileChecked(true);
            });
    };

    // Handle profile setup completion
    const handleProfileSetupComplete = () => {
        setNeedsProfileSetup(false);
    };

    // Toggle registration view
    const handleRegisterClick = () => {
        setShowRegister(true);
    };

    // Handle logout
    const handleLogout = () => {
        setUserId(null);
        setToken(null);
        localStorage.removeItem('userId');
        localStorage.removeItem('token');
        setIsSessionValid(false);
        setShowRegister(false);
        setNeedsProfileSetup(false);
        setShowProfileManager(false);

        // Disconnect socket
        if (window.socket) {
            window.socket.disconnect();
        }
    };

    // Toggle profile manager
    const handleToggleProfileManager = () => {
        setShowProfileManager(!showProfileManager);
    };

    // Toggle login modal
    const handleToggleLoginModal = () => {
        setShowLoginModal(!showLoginModal);
    };

    // Password gate removed — no-op kept for context value
    const handlePasswordCorrect = () => {};

    // Check if current path is an embed route - these bypass ALL gates (password, profile, loading)
    const isEmbedParam = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === 'true';
    const isEmbedPath = typeof window !== 'undefined' && (
        window.location.pathname.startsWith('/embed') ||
        window.location.pathname.startsWith('/qr/') ||
        window.location.pathname.startsWith('/spectate') ||
        window.location.pathname === '/badge-station' ||
        window.location.pathname === '/registration-codes' ||
        window.location.pathname === '/terms' ||
        window.location.pathname === '/reg-video'
    );

    // For embed routes, skip all loading/profile/password checks and render directly
    if (isEmbedPath) {
        return (
            <ProModeProvider initialProMode={true}>
                <NotificationProvider>
                    <Router>
                        <Routes>
                            <Route path="/embed/stock/:ticker" element={<EmbedStockChart />} />
                            <Route path="/embed/stocks/:tickers" element={<EmbedMultiStockChart />} />
                            <Route path="/embed/ticker" element={<EmbedTickerTape />} />
                            <Route path="/embed/events-ticker" element={<EmbedEventsTicker />} />
                            <Route path="/embed/leaderboard" element={<EmbedLeaderboard />} />
                            <Route path="/embed/countdown" element={<EmbedCountdown />} />
                            <Route path="/embed/price/:ticker" element={<EmbedPrice />} />
                            <Route path="/embed/market" element={<EmbedMarket />} />
                            <Route path="/embed/full-market" element={<EmbedMarket fullMarket={true} defaultShowCharts={true} />} />
                            <Route path="/embed/full-market-compact" element={<EmbedMarket fullMarket={true} defaultShowCharts={false} />} />
                            <Route path="/embed/full-market-box" element={<EmbedMarket fullMarket={true} boxStyle={true} defaultShowCharts={true} />} />
                            <Route path="/embed/bottom-ticker" element={<EmbedBottomTicker />} />
                            <Route path="/embed/detail/:ticker" element={<EmbedStockDetail />} />
                            <Route path="/embed/trades" element={<EmbedRecentTrades />} />
                            <Route path="/embed/portfolio/:userId" element={<EmbedPortfolio />} />
                            <Route path="/embed/portfolio" element={<EmbedPortfolio />} />
                            <Route path="/embed/video" element={<EmbedVideo />} />
                            <Route path="/embed/content/:contentId" element={<EmbedContent />} />
                            <Route path="/embed/briefcase" element={<EmbedBriefcaseLeaderboard />} />
                            <Route path="/embed/random-stocks" element={<EmbedRandomStocks />} />
            <Route path="/embed/dashboard" element={<EmbedDashboard />} />
            <Route path="/embed/trades-table" element={<EmbedTradesTable />} />
            <Route path="/embed/events-table" element={<EmbedEventsTable />} />
            <Route path="/embed/random-detail" element={<EmbedRandomDetail />} />
            <Route path="/embed/leaderboard-logo" element={<EmbedLeaderboardLogo />} />
            <Route path="/embed/home" element={<EmbedHome />} />
            <Route path="/embed/shuffle" element={<EmbedShuffle />} />
            <Route path="/embed/multi" element={<EmbedMultiScreen />} />
            <Route path="/embed/reg-video" element={<Navigate to="/embed/content/registration?loop=true&muted=true&autoplay=true" replace />} />
            <Route path="/reg-video" element={<Navigate to="/embed/content/registration?loop=true&muted=true&autoplay=true" replace />} />
                            <Route path="/qr/:code" element={<QRCodeDisplay />} />
                            <Route path="/spectate/tv" element={<BattleshipTVPage />} />
                            <Route path="/spectate/battleship" element={<BattleshipSpectatorPage />} />
                            <Route path="/spectate/deadcat" element={<MinigolfSpectatorPage />} />
                            <Route path="/spectate/briefcase" element={<BriefcaseSpectatorPage />} />
                            <Route path="/spectate/ceremony" element={<CeremonySpectatorPage />} />
                                            <Route path="/badge-station" element={<BadgeStation />} />
                                            <Route path="/registration-codes" element={<RegistrationCodesDisplay />} />
                        </Routes>
                    </Router>
                </NotificationProvider>
            </ProModeProvider>
        );
    }

    // Show loading state while checking session - just return null for instant load
    if (!sessionChecked || !profileChecked) {
        return null;
    }

    // Show profile setup if needed
    if (userId && isSessionValid && needsProfileSetup) {
        return (
            <ProModeProvider initialProMode={true}>
                <NotificationProvider>
                    <div className="app-container profile-setup pro-mode">
                        <ProfileSetup
                            userId={userId}
                            onComplete={handleProfileSetupComplete}
                        />
                    </div>
                </NotificationProvider>
            </ProModeProvider>
        );
    }

    // Build context value with all shared state for child components
    const appState = {
        hasAccess, handlePasswordCorrect,
        activeTab, setActiveTab,
        isSessionValid, userId,
        handleLogout, handleToggleLoginModal, handleToggleProfileManager,
        marketState,
        bigGameAnnouncement, setBigGameAnnouncement,
        adminNotification, setAdminNotification,
        showProfileManager, setShowProfileManager,
        showLoginModal, setShowLoginModal,
        showRegister, setShowRegister,
        handleAuth, handleRegisterClick,
        positions
    };

    // Render the main app with Pro Mode styling
    return (
        <ProModeProvider initialProMode={true}>
            <NotificationProvider>
                <AppStateContext.Provider value={appState}>
                    <Router>
                        <MainRouter />
                    </Router>
                </AppStateContext.Provider>
            </NotificationProvider>
        </ProModeProvider>
    );
}

export default App;